import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import { useRouter } from 'next/router'
import { BuilderComponent, Builder, builder } from '@builder.io/react'
import DefaultErrorPage from 'next/error'
import Head from 'next/head'
// Lazyload these if you we ever actually need them https://github.com/BuilderIO/builder/tree/main/packages/widgets
//import '@builder.io/widgets'
import '../src/modules/cardholder/CardActivate/CardActivate.builder'
import '../src/modules/cardholder/ContactUs/ContactUs.builder'
import '../src/modules/cardholder/VirtualRedemption/VirtualRedemption.builder'
import '../src/common/components/marketing/heros/HomepageHero.builder'
import '../src/common/components/marketing/text/MulticolorText.builder'
import '../src/common/components/marketing/products/ProductCard.builder'
import '../src/common/components/marketing/press/PressCoverage.builder'
import '../src/common/components/marketing/headlines/HeadlineEyebrow.builder'
import '../src/common/components/marketing/cards/ActionCard.builder'
import '../src/common/components/marketing/buttons/Button.builder'
import '../src/common/components/marketing/reviews/Testimonial.builder'
import '../src/common/components/marketing/reviews/StarRating.builder'
import '../src/common/components/marketing/reviews/GoogleReview.builder'
import '../src/common/components/marketing/buttons/LinkButton.builder'
import '../src/common/components/marketing/text/FAQSection.builder'
import '../src/common/components/marketing/text/HandwrittenText.builder'
import '../src/common/components/marketing/cards/PostItNote.builder'
import '../src/common/components/marketing/forms/HubspotLeadsForm.builder'
import '../src/common/components/marketing/images/NextImage.builder'
import '../src/common/components/marketing/text/ToggledColumns.builder'
import '../src/common/components/marketing/cards/StaffBio.builder'
import '../src/common/components/marketing/lists/BrandList.builder'
import '../src/common/components/marketing/modals/ModalGreetingCards.builder'
import '../src/common/components/marketing/modals/Modal.builder'
import getBuilderPageContent from '../src/common/queries/getBuilderPageContent'
import getBuilderPromoBanners from '../src/common/queries/getBuilderPromoBanners'

export async function getStaticProps({
    params,
}: GetStaticPropsContext<{ page: string[] }>) {
    const { content } = await getBuilderPageContent({
        params,
    })
    const { consumerBanner, corporateBanner } = await getBuilderPromoBanners()

    return {
        props: {
            content,
            consumerBanner,
            corporateBanner,
        },
        // Next.js will attempt to re-generate the page:
        // - When a request comes in
        // - At most once every 5 seconds
        revalidate: true,
        notFound: !content,
    }
}

export async function getStaticPaths() {
    const pages = await builder.getAll('page', {
        options: { noTargeting: true },
        omit: 'data.blocks',
        // @TODO look into how to filter pages in API request
        // query: {
        //     data: {
        //         url: { $ne: 'brands' },
        //     },
        // },
    })

    // Filter out the /brands page since we have our own page template
    const filteredPages = pages.filter((page) => {
        return page?.data?.url.includes('/brands') === false
    })

    return {
        paths: filteredPages.map((page) => `${page.data?.url}`),
        fallback: true,
    }
}

export default function Page({
    content,
}: InferGetStaticPropsType<typeof getStaticProps>) {
    const router = useRouter()
    if (router.isFallback) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    minHeight: '75vh',
                }}
            >
                <h1>Loading...</h1>
            </div>
        )
    }

    const isLive = !Builder.isEditing && !Builder.isPreviewing
    if (!content && isLive) {
        return (
            <>
                <Head>
                    <meta name="robots" content="noindex" />
                    <meta name="title" />
                </Head>
                <DefaultErrorPage statusCode={404} />
            </>
        )
    }

    return (
        <>
            <BuilderComponent model="page" content={content} />
        </>
    )
}
